import { useEffect, useId, useRef, useState } from 'react';
import { copy, copyOutcomeText } from '../clipboard.ts';

interface GeneratedPasswordProps {
  /** Whose password it is, as the heading names them. */
  readonly name: string;
  readonly password: string;
  /** Hides the panel. The caller drops its only copy of the password here. */
  readonly onDismiss: () => void;
}

/**
 * The one time a password set by platform staff is visible.
 *
 * The server never returns a password, so this is the browser's own copy of
 * what it just posted, and the caller holds it in state that exists only while
 * this panel does: dismissing it, or leaving the screen, discards it. It is
 * never written to a URL, storage or the console, and it is kept out of the
 * live region — the sentence announcing it is, the characters are not, so a
 * screen reader does not read a credential aloud in an open-plan office.
 *
 * Focus moves here when it appears, so a keyboard user lands on the Copy button
 * rather than having to hunt for a panel that appeared above them.
 */
export const GeneratedPassword = ({ name, password, onDismiss }: GeneratedPasswordProps) => {
  const headingId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLElement>(null);
  const [copyNote, setCopyNote] = useState('');

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const copyPassword = async () => {
    setCopyNote(copyOutcomeText(await copy(password, valueRef.current), 'The password'));
  };

  return (
    <div
      ref={panelRef}
      className="callout callout-caution pf-secret"
      role="region"
      aria-labelledby={headingId}
      tabIndex={-1}
    >
      <strong className="callout-title" id={headingId}>
        {name}’s password — shown once
      </strong>
      <p className="pf-secret-text">
        Copy it now. <strong>It will not be shown again</strong>, and nobody at DriftWatch can look
        it up later. Pass it to {name} through a secure channel — in person, by phone, or through a
        password manager’s sharing — never by email or chat, and ask them to change it from their
        Account page once they have signed in.
      </p>
      <code ref={valueRef} className="mono pf-secret-value" translate="no">
        {password}
      </code>
      <div className="toolbar">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void copyPassword()}>
          Copy password
        </button>
        <button type="button" className="btn btn-sm" onClick={onDismiss}>
          Done — hide it
        </button>
      </div>
      <p className="hint" role="status" aria-live="polite">
        {copyNote}
      </p>
    </div>
  );
};
